import { useSendNotification } from "@app/hooks/useNotification";
import type { CycleTotal } from "@app/lib/compta/calculators/cycle_totals";
import { normalizeSiren } from "@app/lib/compta/referentiels/siren";
import { clientFetch } from "@app/lib/egress/client";
import type { ComptaFindingReviewStatus } from "@app/lib/models/compta/compta_missions";
import type { ComptaDocumentType } from "@app/lib/resources/compta_document_resource";
import type {
  ComptaRelanceGesture,
  ComptaRelanceType,
} from "@app/lib/resources/compta_relance_resource";
import { useFetcher } from "@app/lib/swr/FetcherContext";
import {
  emptyArray,
  useSWRInfiniteWithDefaults,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import type {
  GetComptaCabinetResponseBody,
  PatchComptaCabinetRequestBody,
  PatchComptaCabinetResponseBody,
} from "@app/types/api/compta/cabinet";
import type {
  GetComptaComparativeResponseBody,
  GetComptaCycleTotalsResponseBody,
  GetComptaLedgerResponseBody,
  GetComptaMissionResponseBody,
  GetComptaMissionsResponseBody,
  PostComptaFindingReviewRequestBody,
  PostComptaMissionRequestBody,
  PostComptaMissionResponseBody,
  PutComptaCycleConclusionRequestBody,
} from "@app/types/api/compta/cahier";
import type {
  GetComptaClotureResponseBody,
  PostComptaClotureResponseBody,
} from "@app/types/api/compta/cloture";
import type {
  GetComptaCabinetRelancesResponseBody,
  GetComptaMissingPiecesResponseBody,
  GetComptaRelancesResponseBody,
  PatchComptaRelanceResponseBody,
  PostComptaRelanceResponseBody,
  PostComptaRelanceSendResponseBody,
} from "@app/types/api/compta/collecte";
import type {
  GetComptaCorporateTaxResponseBody,
  PatchComptaCorporateTaxRequestBody,
  PostComptaTaxAdjustmentRequestBody,
} from "@app/types/api/compta/corporate_tax";
import type {
  GetComptaDocumentsResponseBody,
  PostComptaDocumentResponseBody,
} from "@app/types/api/compta/documents";
import type {
  ComptaDossierListItemType,
  GetComptaDossierIdentiteResponseBody,
  GetComptaDossiersResponseBody,
  PatchComptaDossierRequestBody,
  PostComptaDossierRequestBody,
  PostComptaDossierResponseBody,
  PostComptaEntrustRequestBody,
  PostComptaEntrustResponseBody,
} from "@app/types/api/compta/dossiers";
import type {
  ComptaDeadlinesWindow,
  ComptaDeadlineType,
  ComptaDossierWithoutProfileType,
  GetComptaDeadlinesResponseBody,
  PostComptaDeadlineFilingRequestBody,
} from "@app/types/api/compta/echeances";
import type {
  GetComptaFecImportsResponseBody,
  PostComptaFecImportResponseBody,
} from "@app/types/api/compta/fec";
import type {
  ComptaCabinetDocumentFilter,
  ComptaCabinetDocumentType,
  GetComptaCabinetDocumentsResponseBody,
} from "@app/types/api/compta/fichiers";
import type {
  ComptaFiscalProfileType,
  GetComptaFiscalProfileResponseBody,
  PostComptaFiscalProfileRequestBody,
  PostComptaFiscalProfileResponseBody,
} from "@app/types/api/compta/fiscal_profile";
import type { GetComptaOpeningBalanceResponseBody } from "@app/types/api/compta/opening_balance";
import type {
  GetComptaPennylaneConnectionResponseBody,
  PostComptaPennylaneSyncRequestBody,
  PostComptaPennylaneSyncResponseBody,
  PutComptaPennylaneConnectionRequestBody,
  PutComptaPennylaneConnectionResponseBody,
} from "@app/types/api/compta/pennylane";
import type {
  ComptaDashboardPeriod,
  GetComptaDashboardResponseBody,
} from "@app/types/api/compta/tableau_de_bord";
import type {
  ComptaCabinetMissionType,
  GetComptaCabinetMissionsResponseBody,
} from "@app/types/api/compta/travaux";
import type {
  PostInvitationRequestBody,
  PostInvitationResponseBody,
} from "@app/types/api/invitation";
import { isString } from "@app/types/shared/utils/general";
import type { LightWorkspaceType } from "@app/types/user";
import { useCallback, useMemo, useState } from "react";
import type { Fetcher } from "swr";
import { useSWRConfig } from "swr";

// SWR hooks of the cahier (chantier 6) and the poste de revue actions (chantier 7).

export function useComptaMissions({
  owner,
  podId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const missionsFetcher: Fetcher<GetComptaMissionsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/spaces/${podId}/compta/missions`,
    missionsFetcher,
    {
      // A launched mission runs 2-3 minutes in Temporal: poll while one is in
      // flight, stop as soon as none is — otherwise the cahier stays frozen on
      // "0/3 cycles terminés" under a toast that promised it would fill up.
      refreshInterval: (latest: GetComptaMissionsResponseBody | undefined) =>
        latest?.missions.some(
          (m) => m.status === "pending" || m.status === "running"
        )
          ? 5_000
          : 0,
    }
  );

  return {
    hasDossier: data?.hasDossier ?? false,
    dossier: data?.dossier ?? null,
    // Server-computed write capability (chantier 7): default false while loading —
    // never offer a gesture the API has not confirmed.
    canReview: data?.canReview ?? false,
    fiscalYears: data?.fiscalYears ?? emptyArray(),
    missions: data?.missions ?? emptyArray(),
    isMissionsLoading: !error && !data && !disabled,
    isMissionsError: !!error,
    mutateMissions: mutate,
  };
}

export function useComptaMission({
  owner,
  podId,
  missionId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  missionId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const missionFetcher: Fetcher<GetComptaMissionResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled || missionId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/missions/${missionId}`,
    missionFetcher,
    {
      // Same rule as the list: follow a mission while it computes, then stop.
      refreshInterval: (latest: GetComptaMissionResponseBody | undefined) =>
        latest?.mission.status === "pending" ||
        latest?.mission.status === "running"
          ? 5_000
          : 0,
    }
  );

  return {
    mission: data?.mission ?? null,
    isMissionLoading: !error && !data && !disabled && missionId !== null,
    isMissionError: !!error,
    mutateMission: mutate,
  };
}

// Rattacher une pièce à l'écriture qu'elle justifie, depuis Fichiers. La route
// existait ; aucun écran ne l'appelait, et une pièce déposée par le client
// restait « non rattachée » pour toujours (recette de prod du 13/10). L'APPELANT
// revalide (documents et pièces manquantes).
export function useAttachComptaDocument({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isAttaching, setIsAttaching] = useState(false);

  const attachDocument = useCallback(
    async ({
      documentId,
      entryId,
    }: {
      documentId: string;
      entryId: string;
    }): Promise<boolean> => {
      setIsAttaching(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/documents/${documentId}/attachments`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ entryId }),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Pièce non rattachée",
            description:
              response.status === 404
                ? "Cette pièce ou cette écriture n'est plus accessible. Vérifiez vos droits sur ce dossier."
                : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return false;
        }
        sendNotification({
          type: "success",
          title: "Pièce rattachée",
          description: "L'écriture ne figure plus parmi les pièces manquantes.",
        });
        return true;
      } catch {
        sendNotification({
          type: "error",
          title: "Pièce non rattachée",
          description:
            "Le serveur ne répond pas. Vérifiez votre connexion, puis réessayez.",
        });
        return false;
      } finally {
        setIsAttaching(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  return { attachDocument, isAttaching };
}

/**
 * POST the resume of a failed révision, with the cabinet-facing
 * notifications. Shared by the Cahier's button and the Travaux kanban
 * (lot D5b), so both say the same thing.
 */
async function postResumeMission({
  owner,
  podId,
  missionId,
  sendNotification,
}: {
  owner: LightWorkspaceType;
  podId: string;
  missionId: string;
  sendNotification: ReturnType<typeof useSendNotification>;
}): Promise<boolean> {
  try {
    const response = await clientFetch(
      `/api/w/${owner.sId}/spaces/${podId}/compta/missions/${missionId}/resume`,
      { method: "POST" }
    );
    if (!response.ok) {
      sendNotification({
        type: "error",
        title: "Révision non reprise",
        description:
          response.status === 400
            ? "Cette révision ne peut pas être reprise : elle n'est plus en échec, ou les écritures du dossier ont changé depuis son lancement."
            : response.status === 404
              ? "Cette révision n'est plus accessible. Vérifiez vos droits sur ce dossier."
              : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
      });
      return false;
    }
    sendNotification({
      type: "success",
      title: "Révision reprise",
      description:
        "Les cycles en échec sont rejoués ; le Cahier se remplit à mesure.",
    });
    return true;
  } catch {
    sendNotification({
      type: "error",
      title: "Révision non reprise",
      description:
        "Le serveur ne répond pas. Vérifiez votre connexion, puis réessayez.",
    });
    return false;
  }
}

// Reprendre une révision échouée, depuis le Cahier. La route existait depuis
// le chantier 7 ; aucun écran de la coquille ne l'appelait (recette de prod du
// 13/10 : une révision échouée ne se relançait que par le script
// d'exploitation). L'APPELANT revalide, comme pour la revue.
export function useResumeComptaMission({
  owner,
  podId,
  missionId,
}: {
  owner: LightWorkspaceType;
  podId: string;
  missionId: string | null;
}) {
  const sendNotification = useSendNotification();
  const [isResuming, setIsResuming] = useState(false);

  const resumeMission = useCallback(async (): Promise<boolean> => {
    if (missionId === null) {
      return false;
    }
    setIsResuming(true);
    try {
      return await postResumeMission({
        owner,
        podId,
        missionId,
        sendNotification,
      });
    } finally {
      setIsResuming(false);
    }
  }, [owner, podId, missionId, sendNotification]);

  return { resumeMission, isResuming };
}

// Poste de revue (chantier 7): the verdict gesture. On success the CALLER
// revalidates (mutateMission / mutateMissions) — the hook only performs the write.
export function useReviewComptaFinding({
  owner,
  podId,
  missionId,
}: {
  owner: LightWorkspaceType;
  podId: string;
  missionId: string | null;
}) {
  const sendNotification = useSendNotification();
  const [isReviewing, setIsReviewing] = useState(false);

  const reviewFinding = useCallback(
    async ({
      findingId,
      status,
      note,
    }: {
      findingId: string;
      status: ComptaFindingReviewStatus;
      note?: string | null;
    }): Promise<boolean> => {
      if (missionId === null) {
        return false;
      }
      setIsReviewing(true);
      try {
        // `note` stays UNCOALESCED: `JSON.stringify` drops an undefined key, so an
        // absent note reaches the server absent — which means "keep the motive".
        // `?? null` here sent an explicit erasure on every reopening.
        const body: PostComptaFindingReviewRequestBody = {
          findingId,
          status,
          note,
        };
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/missions/${missionId}/review`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Revue non enregistrée",
            // A 400 is a métier refusal (constat or cycle changed under us), not
            // a permission problem: never accuse the user's rights for it.
            description:
              response.status === 400
                ? "Ce geste n'est pas possible sur ce constat (constat modifié entre-temps). Rechargez le cahier."
                : response.status === 404
                  ? "Ce constat n'est plus accessible. Vérifiez vos droits sur cet espace, puis rechargez le cahier."
                  : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return false;
        }
        return true;
      } catch {
        sendNotification({
          type: "error",
          title: "Revue non enregistrée",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return false;
      } finally {
        setIsReviewing(false);
      }
    },
    [missionId, owner.sId, podId, sendNotification]
  );

  return { reviewFinding, isReviewing };
}

// Poste de revue (chantier 7): the cycle conclusion. Same contract as the verdict
// gesture: the caller revalidates on success.
export function useConcludeComptaCycle({
  owner,
  podId,
  missionId,
}: {
  owner: LightWorkspaceType;
  podId: string;
  missionId: string | null;
}) {
  const sendNotification = useSendNotification();
  const [isConcluding, setIsConcluding] = useState(false);

  const concludeCycle = useCallback(
    async ({
      cycleKey,
      conclusion,
    }: {
      cycleKey: string;
      conclusion: string | null;
    }): Promise<boolean> => {
      if (missionId === null) {
        return false;
      }
      setIsConcluding(true);
      try {
        const body: PutComptaCycleConclusionRequestBody = {
          cycleKey,
          conclusion,
        };
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/missions/${missionId}/conclusion`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Conclusion non enregistrée",
            description:
              response.status === 400
                ? "Seul un cycle dont le calcul est terminé peut être conclu. Rechargez le cahier."
                : response.status === 404
                  ? "Ce cycle n'est plus accessible. Vérifiez vos droits sur cet espace, puis rechargez le cahier."
                  : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return false;
        }
        return true;
      } catch {
        sendNotification({
          type: "error",
          title: "Conclusion non enregistrée",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return false;
      } finally {
        setIsConcluding(false);
      }
    },
    [missionId, owner.sId, podId, sendNotification]
  );

  return { concludeCycle, isConcluding };
}

// Poste de revue (chantier 7 PR B): launching a mission from the cahier, and
// resuming a failed one. Both return the mission id on success; the caller
// revalidates and selects it.
export function usePilotComptaMission({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isPiloting, setIsPiloting] = useState(false);

  const post = useCallback(
    async ({
      path,
      body,
      failureTitle,
      refusedDescription,
    }: {
      path: string;
      body?: PostComptaMissionRequestBody;
      failureTitle: string;
      // What a 400 means for THIS gesture: launching and resuming are refused for
      // different reasons, and a shared sentence would state false causes.
      refusedDescription: string;
    }): Promise<{ missionId: string } | null> => {
      setIsPiloting(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/${path}`,
          {
            method: "POST",
            ...(body
              ? {
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(body),
                }
              : {}),
          }
        );
        if (!response.ok) {
          // Three distinct causes, three distinct sentences: a métier refusal (400)
          // the user can act on, an access problem (404), and a server failure
          // (5xx) — which must never be blamed on the user's rights.
          const description =
            response.status === 400
              ? refusedDescription
              : response.status === 404
                ? "Ce dossier ou cette mission n'est plus accessible. Vérifiez vos droits sur cet espace, puis rechargez le cahier."
                : "Le service de révision est momentanément indisponible. Réessayez dans quelques minutes ; si le problème persiste, prévenez un administrateur.";
          sendNotification({
            type: "error",
            title: failureTitle,
            description,
          });
          return null;
        }
        // Typed rather than cast: the contract belongs to the route.
        const payload: PostComptaMissionResponseBody = await response.json();
        return payload;
      } catch {
        sendNotification({
          type: "error",
          title: failureTitle,
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsPiloting(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  const launchMission = useCallback(
    async (fiscalYearId: string) => {
      const result = await post({
        path: "missions",
        body: { fiscalYearId },
        failureTitle: "Mission non lancée",
        refusedDescription:
          "Cet exercice ne peut pas être lancé : il n'est pas encore clos, ou une mission y est déjà en cours. Rechargez le cahier.",
      });
      if (result) {
        sendNotification({
          type: "success",
          title: "Mission lancée",
          description:
            "Les cycles sont en cours de calcul — le cahier se remplira au fil de l'eau.",
        });
      }
      return result;
    },
    [post, sendNotification]
  );

  const resumeMission = useCallback(
    async (missionId: string) => {
      const result = await post({
        path: `missions/${missionId}/resume`,
        failureTitle: "Mission non reprise",
        refusedDescription:
          "Cette mission ne peut pas être reprise : elle n'est plus en échec (elle a peut-être déjà été reprise), ou le grand livre du dossier a changé depuis son lancement. Dans ce dernier cas, lancez une nouvelle révision sur l'exercice pour repartir du grand livre à jour.",
      });
      if (result) {
        sendNotification({
          type: "success",
          title: "Mission reprise",
          description:
            "Les cycles non terminés vont être rejoués ; ceux qui ont abouti gardent leur feuille, leurs constats, vos verdicts et vos conclusions.",
        });
      }
      return result;
    },
    [post, sendNotification]
  );

  return { launchMission, resumeMission, isPiloting };
}

// The dossier's import history (chantier 10.a) — refused sources included, they
// are audit trail.
export function useComptaFecImports({
  owner,
  podId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const importsFetcher: Fetcher<GetComptaFecImportsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/spaces/${podId}/compta/fec-imports`,
    importsFetcher
  );

  return {
    sources: data?.sources ?? emptyArray(),
    isSourcesLoading: !error && !data && !disabled,
    isSourcesError: !!error,
    mutateSources: mutate,
  };
}

// The exercice's missing pieces (chantier 11.a): the relance raw material,
// derived from the accounting itself.
export function useComptaMissingPieces({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const missingPiecesFetcher: Fetcher<GetComptaMissingPiecesResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled || fiscalYearId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/missing-pieces?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
    missingPiecesFetcher
  );

  return {
    missingPieces: data?.missingPieces ?? null,
    isMissingPiecesLoading:
      !error && !data && !disabled && fiscalYearId !== null,
    isMissingPiecesError: !!error,
    mutateMissingPieces: mutate,
  };
}

// Le comparatif N-1 (chantier 13.c) — the analytical review's raw material.
export function useComptaComparative({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const comparativeFetcher: Fetcher<GetComptaComparativeResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    disabled || fiscalYearId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/comparative?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
    comparativeFetcher
  );

  return {
    comparative: data?.comparative ?? null,
    isComparativeLoading: !error && !data && !disabled && fiscalYearId !== null,
    isComparativeError: !!error,
  };
}

// The dossier's relances (chantier 11.a) — the drafted requests and their
// canonical state (draft / sent / answered), newest first.
export function useComptaRelances({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const relancesFetcher: Fetcher<GetComptaRelancesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled || fiscalYearId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/relances?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
    relancesFetcher
  );

  return {
    relances: data?.relances ?? emptyArray(),
    isRelancesLoading: !error && !data && !disabled && fiscalYearId !== null,
    isRelancesError: !!error,
    mutateRelances: mutate,
  };
}

// The relance write gestures (chantier 11.a): draft creation freezes the
// missing-pieces list; the send is a HUMAN act the app only records (D4).
export function useComptaRelanceActions({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isRelanceWorking, setIsRelanceWorking] = useState(false);

  const createRelance = useCallback(
    async ({ fiscalYearId }: { fiscalYearId: string }) => {
      setIsRelanceWorking(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/relances`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fiscalYearId }),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Relance impossible",
            description:
              response.status === 409
                ? "Une relance est déjà en cours pour cet exercice : traitez-la (envoyée puis répondue) avant d'en préparer une nouvelle."
                : response.status === 400
                  ? "Aucune pièce ne manque sur cet exercice : rien à demander."
                  : "Le service est momentanément indisponible. Réessayez.",
          });
          return null;
        }
        const payload: PostComptaRelanceResponseBody = await response.json();
        return payload.relance;
      } catch {
        sendNotification({
          type: "error",
          title: "Relance impossible",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsRelanceWorking(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  const applyRelanceGesture = useCallback(
    async ({
      relanceId,
      ...gesture
    }: { relanceId: string } & ComptaRelanceGesture) => {
      setIsRelanceWorking(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/relances/${relanceId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(gesture),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Geste impossible",
            description:
              response.status === 409
                ? "L'état de la relance a changé entre-temps : rechargez la liste."
                : "Le service est momentanément indisponible. Réessayez.",
          });
          return null;
        }
        const payload: PatchComptaRelanceResponseBody = await response.json();
        return payload.relance;
      } catch {
        sendNotification({
          type: "error",
          title: "Geste impossible",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsRelanceWorking(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  // Relances R2: the real send. No notification here: the panel shows the
  // server's French message next to the button.
  const sendRelance = useCallback(
    async (
      relanceId: string
    ): Promise<
      | { ok: true; relance: ComptaRelanceType }
      | {
          ok: false;
          message: string;
          code: string | null;
          sentAt: string | null;
          sentByName: string | null;
        }
    > => {
      setIsRelanceWorking(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/relances/${relanceId}/send`,
          { method: "POST" }
        );
        const payload: unknown = await response.json().catch(() => null);
        const field = (key: string): string | null => {
          const value =
            payload && typeof payload === "object"
              ? (payload as Record<string, unknown>)[key]
              : null;
          return typeof value === "string" ? value : null;
        };
        if (!response.ok) {
          return {
            ok: false,
            message:
              field("error") ??
              (response.status === 404
                ? "Cette relance n'existe plus. Rechargez la liste."
                : "Le service est momentanément indisponible. Réessayez."),
            code: field("code"),
            sentAt: field("sentAt"),
            sentByName: field("sentByName"),
          };
        }
        return {
          ok: true,
          relance: (payload as PostComptaRelanceSendResponseBody).relance,
        };
      } catch {
        return {
          ok: false,
          message: "Le serveur n'a pas répondu. Réessayez.",
          code: null,
          sentAt: null,
          sentByName: null,
        };
      } finally {
        setIsRelanceWorking(false);
      }
    },
    [owner.sId, podId]
  );

  return { createRelance, applyRelanceGesture, sendRelance, isRelanceWorking };
}

// The FEC upload (chantier 10.a). Returns the FULL report — including on a
// business refusal (status "failed"): the dialog renders it like the CLI prints
// it; only transport/HTTP failures surface as notifications here. The caller
// revalidates (missions + sources) on an "imported" report.
export function useImportComptaFec({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isImporting, setIsImporting] = useState(false);

  const importFec = useCallback(
    async ({
      file,
      startDate,
      endDate,
      supersede,
    }: {
      file: File;
      startDate?: string;
      endDate?: string;
      supersede?: boolean;
    }): Promise<PostComptaFecImportResponseBody["report"] | null> => {
      setIsImporting(true);
      try {
        const searchParams = new URLSearchParams();
        if (startDate && endDate) {
          searchParams.set("startDate", startDate);
          searchParams.set("endDate", endDate);
        }
        if (supersede) {
          searchParams.set("supersede", "true");
        }
        const query = searchParams.size > 0 ? `?${searchParams}` : "";
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/fec-imports${query}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/octet-stream",
              // The file name travels in a header, never in the query [SEC1]:
              // it carries the client's SIREN.
              "x-compta-file-name": file.name,
            },
            body: file,
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Import impossible",
            description:
              response.status === 400
                ? "La requête d'import est invalide (nom de fichier ou bornes d'exercice). Vérifiez le fichier choisi."
                : response.status === 404
                  ? "Ce dossier n'est plus accessible. Vérifiez vos droits sur cet espace."
                  : response.status === 413
                    ? "Le fichier dépasse la taille maximale d'import (100 Mo)."
                    : "Le service d'import est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PostComptaFecImportResponseBody = await response.json();
        return payload.report;
      } catch {
        sendNotification({
          type: "error",
          title: "Import impossible",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsImporting(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  return { importFec, isImporting };
}

// The pièces of one entry (chantier 10.b — the proof panel of the drill).
export function useComptaDocuments({
  owner,
  podId,
  entryId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  entryId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const documentsFetcher: Fetcher<GetComptaDocumentsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled || entryId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/documents?entryId=${encodeURIComponent(entryId)}`,
    documentsFetcher
  );

  return {
    documents: data?.documents ?? emptyArray(),
    isDocumentsLoading: !error && !data && !disabled && entryId !== null,
    isDocumentsError: !!error,
    mutateDocuments: mutate,
  };
}

// Uploading one pièce (chantier 10.b). Same transport conventions as the FEC
// upload: bytes in the body, name in a header [SEC1]. The caller revalidates.
export function useUploadComptaDocument({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isUploading, setIsUploading] = useState(false);

  const uploadDocument = useCallback(
    async ({
      file,
      pieceRef,
    }: {
      file: File;
      pieceRef?: string | null;
    }): Promise<PostComptaDocumentResponseBody | null> => {
      setIsUploading(true);
      try {
        const query = pieceRef
          ? `?pieceRef=${encodeURIComponent(pieceRef)}`
          : "";
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/documents${query}`,
          {
            method: "POST",
            headers: {
              // The browser knows the file's type; the server allowlists it.
              "Content-Type": file.type || "application/octet-stream",
              "x-compta-file-name": file.name,
            },
            body: file,
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Pièce non enregistrée",
            description:
              response.status === 400
                ? "Format non pris en charge : envoyez un PDF ou une photo (PNG, JPEG) de 20 Mo maximum."
                : response.status === 404
                  ? "Ce dossier n'est plus accessible. Vérifiez vos droits sur cet espace."
                  : response.status === 413
                    ? "La pièce dépasse la taille maximale (20 Mo)."
                    : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PostComptaDocumentResponseBody = await response.json();
        sendNotification({
          type: "success",
          title: payload.deduplicated
            ? "Pièce déjà présente"
            : "Pièce enregistrée",
          description: payload.deduplicated
            ? "Ce fichier exact existait déjà dans le dossier — il a été réutilisé."
            : payload.document.attachedEntryCount > 0
              ? `Rattachée à ${payload.document.attachedEntryCount} écriture(s) portant la référence ${payload.document.pieceRef ?? ""}.`
              : "Enregistrée — elle s'attachera aux écritures portant sa référence de pièce.",
        });
        return payload;
      } catch {
        sendNotification({
          type: "error",
          title: "Pièce non enregistrée",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  return { uploadDocument, isUploading };
}

export type ComptaLedgerTarget = {
  fiscalYearId: string;
  // For the sheet's header: the reader must always know WHICH exercice they are
  // drilling (the sheet covers the page's own header).
  fiscalYearStart: string;
  fiscalYearEnd: string;
  accountNumber: string;
  auxNumber: string | null;
  // The "(sans compte auxiliaire)" drill: only the aux-less lines.
  withoutThirdParty: boolean;
};

const LEDGER_PAGE_SIZE = 100;

// The drill's grand livre, keyset-paginated (idiome: usePodConversations).
export function useComptaLedger({
  owner,
  podId,
  target,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  target: ComptaLedgerTarget | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const ledgerFetcher: Fetcher<GetComptaLedgerResponseBody> = fetcher;

  const { data, error, size, setSize, isValidating } =
    useSWRInfiniteWithDefaults(
      (
        _pageIndex: number,
        previousPageData: GetComptaLedgerResponseBody | null
      ) => {
        if (!target) {
          return null;
        }
        const searchParams = new URLSearchParams({
          fiscalYearId: target.fiscalYearId,
          accountNumber: target.accountNumber,
          limit: LEDGER_PAGE_SIZE.toString(),
        });
        if (target.auxNumber) {
          searchParams.set("auxNumber", target.auxNumber);
        }
        if (target.withoutThirdParty) {
          searchParams.set("withoutThirdParty", "true");
        }
        if (previousPageData !== null) {
          if (!previousPageData.ledger.nextCursor) {
            return null;
          }
          searchParams.set("cursor", previousPageData.ledger.nextCursor);
        }
        return `/api/w/${owner.sId}/spaces/${podId}/compta/ledger?${searchParams.toString()}`;
      },
      ledgerFetcher,
      {
        revalidateAll: false,
        revalidateOnFocus: false,
        disabled,
      }
    );

  const rows = useMemo(() => {
    if (!data) {
      return emptyArray<
        GetComptaLedgerResponseBody["ledger"]["rows"][number]
      >();
    }
    return data.flatMap((page) => page.ledger.rows);
  }, [data]);

  const account = data?.[0]?.ledger.account ?? null;
  const thirdParty = data?.[0]?.ledger.thirdParty ?? null;
  const totals = data?.[0]?.ledger.totals ?? null;
  const hasMore = data
    ? (data[data.length - 1]?.ledger.hasMore ?? false)
    : false;

  const loadMore = useCallback(() => {
    if (hasMore && !isValidating) {
      void setSize(size + 1);
    }
  }, [hasMore, isValidating, setSize, size]);

  return {
    ledgerRows: rows,
    ledgerAccount: account,
    ledgerThirdParty: thirdParty,
    ledgerTotals: totals,
    isLedgerLoading: !error && !data && !disabled && target !== null,
    isLedgerError: !!error,
    isLoadingMore: isValidating && size > 1,
    hasMore,
    loadMore,
  };
}

// ————— The dossiers of the CABINET (série 2, lot A1) —————
//
// Cabinet level: `/api/w/:wId/compta/dossiers`, the list the portfolio and the
// client directory render, and the creation of a dossier from a SIREN. Same
// shape as every hook above (audit of 09/10): named params, a typed fetcher,
// `emptyArray()` for a stable default, a domain-named return.

export function useComptaDossiers({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const dossiersFetcher: Fetcher<GetComptaDossiersResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/dossiers`,
    dossiersFetcher
  );

  return {
    dossiers: useMemo(
      () => data?.dossiers ?? emptyArray<ComptaDossierListItemType>(),
      [data]
    ),
    isDossiersLoading: !error && !data && !disabled,
    isDossiersError: !!error,
    mutateDossiers: mutate,
  };
}

// The INSEE identity preview of the creation form. The key stays null until the
// field holds nine digits: no request per keystroke, and never a request for a
// SIREN the server would refuse anyway.
export function useComptaDossierIdentite({
  owner,
  siren,
}: {
  owner: LightWorkspaceType;
  siren: string;
}) {
  const { fetcher } = useFetcher();
  const identiteFetcher: Fetcher<GetComptaDossierIdentiteResponseBody> =
    fetcher;
  // The same shape as the route: no request for a SIREN the server refuses.
  const compact = normalizeSiren(siren);

  const { data, error } = useSWRWithDefaults(
    compact === null
      ? null
      : `/api/w/${owner.sId}/compta/dossiers/identite?siren=${encodeURIComponent(compact)}`,
    identiteFetcher
  );

  return {
    identite: data?.identite ?? null,
    isIdentiteLoading: compact !== null && !error && !data,
    // A 404 (unknown SIREN) and a 503 (référentiel down) both land here: the
    // form says « identité introuvable » and lets the user retry, it does not
    // invent a company.
    isIdentiteError: !!error,
  };
}

export function useCreateComptaDossier({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const [isCreating, setIsCreating] = useState(false);

  const createDossier = useCallback(
    async ({
      siren,
      contactName,
      contactEmail,
    }: {
      siren: string;
      contactName?: string;
      contactEmail?: string;
    }): Promise<ComptaDossierListItemType | null> => {
      setIsCreating(true);
      try {
        const body: PostComptaDossierRequestBody = {
          siren,
          ...(contactName?.trim() ? { contactName: contactName.trim() } : {}),
          ...(contactEmail?.trim()
            ? { contactEmail: contactEmail.trim() }
            : {}),
        };
        const response = await clientFetch(
          `/api/w/${owner.sId}/compta/dossiers`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Dossier non créé",
            description:
              response.status === 400
                ? body.contactEmail
                  ? "Vérifiez le SIREN (neuf chiffres) et le courriel du contact."
                  : "Un SIREN comporte neuf chiffres. Vérifiez la saisie."
                : response.status === 404
                  ? "Aucune entreprise n'est enregistrée sous ce SIREN à l'INSEE."
                  : response.status === 409
                    ? "Un dossier existe déjà pour cette entreprise."
                    : response.status === 503
                      ? "Le référentiel des entreprises ne répond pas. Réessayez dans quelques minutes."
                      : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PostComptaDossierResponseBody = await response.json();
        return payload.dossier;
      } catch {
        sendNotification({
          type: "error",
          title: "Dossier non créé",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [owner.sId, sendNotification]
  );

  return { createDossier, isCreating };
}

// ————— The fiscal profile of a FISCAL YEAR (série 2, lot A3) —————
//
// Read and written from the client fiche. The key names the exercice: there is
// no "current profile of the dossier", here either.

export function useComptaFiscalProfile({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const profileFetcher: Fetcher<GetComptaFiscalProfileResponseBody> = fetcher;
  const { data, error, mutate } = useSWRWithDefaults(
    disabled || fiscalYearId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/fiscal-profile?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
    profileFetcher
  );
  return {
    lecture: data ?? null,
    isProfileLoading: !error && !data && !disabled && fiscalYearId !== null,
    isProfileError: !!error,
    mutateProfile: mutate,
  };
}

export function useCreateComptaFiscalProfile({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isCreating, setIsCreating] = useState(false);

  const createProfile = useCallback(
    async (
      body: PostComptaFiscalProfileRequestBody
    ): Promise<ComptaFiscalProfileType | null> => {
      setIsCreating(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/fiscal-profile`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          // 400 and 409 carry the Resource's French message, written for the
          // cabinet (closed exercice, suppressed regime, IR option, date).
          const payload: unknown = await response.json().catch(() => null);
          const message =
            typeof payload === "object" &&
            payload !== null &&
            "error" in payload &&
            typeof payload.error === "object" &&
            payload.error !== null &&
            "message" in payload.error &&
            typeof payload.error.message === "string" &&
            (response.status === 400 || response.status === 409)
              ? payload.error.message
              : "Le service est momentanément indisponible. Réessayez dans quelques minutes.";
          sendNotification({
            type: "error",
            title: "Profil fiscal non enregistré",
            description: message,
          });
          return null;
        }
        const payload: PostComptaFiscalProfileResponseBody =
          await response.json();
        return payload.profile;
      } catch {
        sendNotification({
          type: "error",
          title: "Profil fiscal non enregistré",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  return { createProfile, isCreating };
}

// ————— The dossier's pièces, all of them (série 2, lot B1) —————
//
// The documents route lists the whole dossier without `entryId`; this is that
// listing, for Dossier · Fichiers (`useComptaDocuments` stays per entry).
export function useComptaDossierDocuments({
  owner,
  podId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const documentsFetcher: Fetcher<GetComptaDocumentsResponseBody> = fetcher;
  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/spaces/${podId}/compta/documents`,
    documentsFetcher
  );
  return {
    documents: useMemo(
      () => data?.documents ?? emptyArray<ComptaDocumentType>(),
      [data]
    ),
    isDocumentsLoading: !error && !data && !disabled,
    isDocumentsError: !!error,
    mutateDocuments: mutate,
  };
}

// ————— The cycle totals of a mission (série 2, lot B3) —————
//
// The three figures of each Cahier cycle row, summed by the server's cycle
// totals calculator [FR2]; the screen only formats them.
export function useComptaCycleTotals({
  owner,
  podId,
  missionId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  missionId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const totalsFetcher: Fetcher<GetComptaCycleTotalsResponseBody> = fetcher;
  const { data, error } = useSWRWithDefaults(
    disabled || missionId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/missions/${encodeURIComponent(missionId)}/cycle-totals`,
    totalsFetcher
  );
  return {
    totals: useMemo(() => data?.totals ?? emptyArray<CycleTotal>(), [data]),
    isTotalsLoading: !error && !data && !disabled && missionId !== null,
    isTotalsError: !!error,
  };
}

// The dossier's Pennylane connection (série 2, lot B4b): its company id and
// date, or null. The token never comes back from the server.
export function useComptaPennylaneConnection({
  owner,
  podId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const connectionFetcher: Fetcher<GetComptaPennylaneConnectionResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/spaces/${podId}/compta/pennylane`,
    connectionFetcher
  );

  return {
    connection: data?.connection ?? null,
    isConnectionLoading: !error && !data && !disabled,
    isConnectionError: !!error,
    mutateConnection: mutate,
  };
}

// Connect, disconnect and sync. Pennylane is only ever READ: the server's
// reader has no write method. The token goes in the PUT body [SEC1].
export function useComptaPennylaneActions({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isWorking, setIsWorking] = useState(false);
  const url = `/api/w/${owner.sId}/spaces/${podId}/compta/pennylane`;

  const call = useCallback(
    async (
      path: string,
      init: RequestInit,
      title: string,
      messageForStatus: (status: number) => string
    ): Promise<Response | null> => {
      setIsWorking(true);
      try {
        const response = await clientFetch(path, init);
        if (!response.ok) {
          sendNotification({
            type: "error",
            title,
            description: messageForStatus(response.status),
          });
          return null;
        }
        return response;
      } catch {
        sendNotification({
          type: "error",
          title,
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [sendNotification]
  );

  const connect = useCallback(
    async (
      body: PutComptaPennylaneConnectionRequestBody
    ): Promise<
      PutComptaPennylaneConnectionResponseBody["connection"] | null
    > => {
      const response = await call(
        url,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        "Connexion à Pennylane impossible",
        (status) =>
          status === 400
            ? "Pennylane n'a pas accepté ce jeton, ou n'a pas répondu. Vérifiez le jeton et, pour un jeton de cabinet, l'identifiant de la société."
            : status === 404
              ? "Ce dossier n'est plus accessible. Vérifiez vos droits sur cet espace."
              : "Le service est momentanément indisponible. Réessayez dans quelques minutes."
      );
      if (!response) {
        return null;
      }
      const payload: PutComptaPennylaneConnectionResponseBody =
        await response.json();
      return payload.connection;
    },
    [call, url]
  );

  const disconnect = useCallback(async (): Promise<boolean> => {
    const response = await call(
      url,
      { method: "DELETE" },
      "Déconnexion impossible",
      (status) =>
        status === 404
          ? "Ce dossier n'est plus accessible. Vérifiez vos droits sur cet espace."
          : "Le service est momentanément indisponible. Réessayez dans quelques minutes."
    );
    return response !== null;
  }, [call, url]);

  const sync = useCallback(
    async (
      body: PostComptaPennylaneSyncRequestBody
    ): Promise<PostComptaPennylaneSyncResponseBody | null> => {
      const response = await call(
        `${url}/sync`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        "Synchronisation impossible",
        (status) =>
          status === 400
            ? "Ce dossier n'est pas connecté à Pennylane, ou les bornes de l'exercice sont invalides."
            : status === 404
              ? "Ce dossier n'est plus accessible. Vérifiez vos droits sur cet espace."
              : status === 502
                ? "Pennylane n'a pas répondu. Réessayez dans quelques minutes."
                : "Le service est momentanément indisponible. Réessayez dans quelques minutes."
      );
      if (!response) {
        return null;
      }
      const payload: PostComptaPennylaneSyncResponseBody =
        await response.json();
      return { report: payload.report, documents: payload.documents };
    },
    [call, url]
  );

  return { connect, disconnect, sync, isWorking };
}

// The cabinet's relances (série 2, lot D1): every relance of every dossier the
// caller can read, newest first, each tagged with its space and client name.
// Read-only: the gestures go through `useComptaRelanceActions`, per dossier.
export function useComptaCabinetRelances({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const relancesFetcher: Fetcher<GetComptaCabinetRelancesResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/relances`,
    relancesFetcher
  );

  return {
    relances: useMemo(
      () =>
        data?.relances ??
        emptyArray<GetComptaCabinetRelancesResponseBody["relances"][number]>(),
      [data]
    ),
    isRelancesLoading: !error && !data && !disabled,
    isRelancesError: !!error,
    mutateRelances: mutate,
  };
}

// The cabinet's identity (série 2, onboarding admin, lots C3+C4).
export function useComptaCabinet({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const cabinetFetcher: Fetcher<GetComptaCabinetResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/cabinet`,
    cabinetFetcher
  );

  return {
    cabinet: data?.cabinet ?? null,
    isCabinetLoading: !error && !data && !disabled,
    isCabinetError: !!error,
    mutateCabinet: mutate,
  };
}

// Onboarding actions: the cabinet's identity, then the team's invitations
// (upstream's route, one role per row: Associé = admin, Collaborateur = user).
export function useComptaOnboardingActions({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const [isWorking, setIsWorking] = useState(false);

  const updateCabinet = useCallback(
    async (
      body: PatchComptaCabinetRequestBody
    ): Promise<PatchComptaCabinetResponseBody["cabinet"] | null> => {
      setIsWorking(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/compta/cabinet`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Cabinet non enregistré",
            description:
              response.status === 400
                ? "Le nom ou la ville n'est pas valide. Le nom : lettres, chiffres et ponctuation des raisons sociales. La ville : lettres, espaces, apostrophes et traits d'union."
                : response.status === 403
                  ? "Seul un associé peut modifier le cabinet."
                  : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PatchComptaCabinetResponseBody = await response.json();
        return payload.cabinet;
      } catch {
        sendNotification({
          type: "error",
          title: "Cabinet non enregistré",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [owner.sId, sendNotification]
  );

  const inviteTeam = useCallback(
    async (
      invitations: PostInvitationRequestBody
    ): Promise<PostInvitationResponseBody | null> => {
      setIsWorking(true);
      try {
        const response = await clientFetch(`/api/w/${owner.sId}/invitations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(invitations),
        });
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Invitations non envoyées",
            description:
              response.status === 403
                ? "Vos droits ne permettent pas ces invitations : seul un associé invite un autre associé."
                : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PostInvitationResponseBody = await response.json();
        return payload;
      } catch {
        sendNotification({
          type: "error",
          title: "Invitations non envoyées",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [owner.sId, sendNotification]
  );

  return { updateCabinet, inviteTeam, isWorking };
}

// The dossiers entrusted to the caller (série 2, lot C5): those whose space
// they are a member of.
export function useComptaDossiersConfies({
  owner,
  disabled,
}: {
  owner: { sId: string };
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const dossiersFetcher: Fetcher<GetComptaDossiersResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/dossiers?confies=true`,
    dossiersFetcher
  );

  return {
    dossiersConfies: data?.dossiers ?? emptyArray(),
    isDossiersConfiesLoading: !error && !data && !disabled,
    isDossiersConfiesError: !!error,
  };
}

// Entrusting dossiers to a member or an invited person (série 2, lot C5).
export function useComptaEntrust({ owner }: { owner: LightWorkspaceType }) {
  const sendNotification = useSendNotification();
  const [isEntrusting, setIsEntrusting] = useState(false);

  const entrust = useCallback(
    async (
      body: PostComptaEntrustRequestBody
    ): Promise<PostComptaEntrustResponseBody | null> => {
      setIsEntrusting(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/compta/dossiers/confier`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Dossier non confié",
            description:
              response.status === 404
                ? "Cette personne n'est ni membre du cabinet ni invitée, ou le dossier n'est plus accessible."
                : response.status === 403
                  ? "Seul un associé, ou la personne qui a créé le dossier, peut le confier."
                  : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PostComptaEntrustResponseBody = await response.json();
        return payload;
      } catch {
        sendNotification({
          type: "error",
          title: "Dossier non confié",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsEntrusting(false);
      }
    },
    [owner.sId, sendNotification]
  );

  return { entrust, isEntrusting };
}

// The closing checklist of a fiscal year (série 2, lot D3).
export function useComptaCloture({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const clotureFetcher: Fetcher<GetComptaClotureResponseBody> = fetcher;
  const inactif = disabled || fiscalYearId === null;

  const { data, error, mutate } = useSWRWithDefaults(
    inactif
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/cloture?fiscalYearId=${encodeURIComponent(fiscalYearId ?? "")}`,
    clotureFetcher
  );

  return {
    checklist: data?.checklist ?? null,
    isClotureLoading: !error && !data && !inactif,
    isClotureError: !!error,
    mutateCloture: mutate,
  };
}

// Closing the fiscal year: final ([FR7]), re-checked by the server.
export function useCloturerExercice({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const [isCloturing, setIsCloturing] = useState(false);

  const cloturer = useCallback(
    async (
      fiscalYearId: string
    ): Promise<PostComptaClotureResponseBody["checklist"] | null> => {
      setIsCloturing(true);
      try {
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/cloture`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ fiscalYearId }),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Exercice non clôturé",
            description:
              response.status === 409
                ? "La liste de contrôle n'est plus complète, ou l'exercice est déjà clôturé. Rechargez la page."
                : response.status === 404
                  ? "Ce dossier n'est plus accessible en écriture. Vérifiez vos droits sur cet espace."
                  : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return null;
        }
        const payload: PostComptaClotureResponseBody = await response.json();
        return payload.checklist;
      } catch {
        sendNotification({
          type: "error",
          title: "Exercice non clôturé",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsCloturing(false);
      }
    },
    [owner.sId, podId, sendNotification]
  );

  return { cloturer, isCloturing };
}

// The cabinet corrects a dossier by hand: its postal code (série 2, lot D4, the
// échéancier reads the département from it) and its contact (relances R1, the
// default recipient of relances). Refreshes the cabinet list, which carries
// both, once the server has them.
export function useUpdateComptaDossier({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const { mutate } = useSWRConfig();
  const [isUpdating, setIsUpdating] = useState(false);

  const updatePostalCode = useCallback(
    async (postalCode: string | null): Promise<void> => {
      setIsUpdating(true);
      try {
        const body: PatchComptaDossierRequestBody = { postalCode };
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/dossier`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Code postal non enregistré",
            description:
              response.status === 404
                ? "Ce dossier n'est plus accessible en écriture. Vérifiez vos droits sur cet espace."
                : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return;
        }
        await mutate(`/api/w/${owner.sId}/compta/dossiers`);
      } catch {
        sendNotification({
          type: "error",
          title: "Code postal non enregistré",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [mutate, owner.sId, podId, sendNotification]
  );

  // Relances R1: an empty field is sent as null, which erases it.
  const updateContact = useCallback(
    async ({
      nom,
      email,
    }: {
      nom: string;
      email: string;
    }): Promise<boolean> => {
      setIsUpdating(true);
      try {
        const body: PatchComptaDossierRequestBody = {
          contactName: nom.trim() === "" ? null : nom.trim(),
          contactEmail: email.trim() === "" ? null : email.trim(),
        };
        const response = await clientFetch(
          `/api/w/${owner.sId}/spaces/${podId}/compta/dossier`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        if (!response.ok) {
          sendNotification({
            type: "error",
            title: "Contact non enregistré",
            description:
              response.status === 400
                ? "Ce courriel n'est pas valide. Vérifiez la saisie."
                : response.status === 404
                  ? "Ce dossier n'est plus accessible en écriture. Vérifiez vos droits sur cet espace."
                  : "Le service est momentanément indisponible. Réessayez dans quelques minutes.",
          });
          return false;
        }
        await mutate(`/api/w/${owner.sId}/compta/dossiers`);
        return true;
      } catch {
        sendNotification({
          type: "error",
          title: "Contact non enregistré",
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    [mutate, owner.sId, podId, sendNotification]
  );

  return { updatePostalCode, updateContact, isUpdating };
}

// The cabinet's échéancier (série 2, lot D4): every deadline of every dossier
// the caller can read, computed by the server on the fly. Read-only; the
// « déposée » gesture is lot D4b.
export function useComptaDeadlines({
  owner,
  window,
  disabled,
}: {
  owner: LightWorkspaceType;
  window: ComptaDeadlinesWindow;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const deadlinesFetcher: Fetcher<GetComptaDeadlinesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/echeances?window=${window}`,
    deadlinesFetcher
  );

  return {
    deadlines: useMemo(
      () => data?.deadlines ?? emptyArray<ComptaDeadlineType>(),
      [data]
    ),
    dossiersWithoutProfile: useMemo(
      () =>
        data?.dossiersWithoutProfile ??
        emptyArray<ComptaDossierWithoutProfileType>(),
      [data]
    ),
    today: data?.today ?? null,
    isDeadlinesLoading: !error && !data && !disabled,
    isDeadlinesError: !!error,
    mutateDeadlines: mutate,
  };
}

/** The deadlines of ONE dossier (série 2, lot D4b), for its Aperçu. */
export function useComptaDossierDeadlines({
  owner,
  podId,
  window,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  window: ComptaDeadlinesWindow;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const deadlinesFetcher: Fetcher<GetComptaDeadlinesResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/echeances?window=${window}`,
    deadlinesFetcher
  );

  return {
    deadlines: useMemo(
      () => data?.deadlines ?? emptyArray<ComptaDeadlineType>(),
      [data]
    ),
    today: data?.today ?? null,
    isDeadlinesLoading: !error && !data && !disabled,
    isDeadlinesError: !!error,
    mutateDeadlines: mutate,
  };
}

/**
 * The « Déposée » gesture (série 2, lot D4b): file or unfile a deadline of a
 * dossier, then revalidate every deadlines list (cabinet and dossier).
 */
export function useComptaDeadlineActions({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const { mutate } = useSWRConfig();
  const [isFiling, setIsFiling] = useState(false);

  const revalidate = useCallback(
    () => mutate((key) => isString(key) && key.includes("/compta/echeances")),
    [mutate]
  );

  const call = useCallback(
    async (
      path: string,
      init: RequestInit,
      title: string,
      messageForStatus: (status: number) => string
    ): Promise<boolean> => {
      setIsFiling(true);
      try {
        const response = await clientFetch(path, init);
        if (!response.ok) {
          sendNotification({
            type: "error",
            title,
            description: messageForStatus(response.status),
          });
          return false;
        }
        await revalidate();
        return true;
      } catch {
        sendNotification({
          type: "error",
          title,
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return false;
      } finally {
        setIsFiling(false);
      }
    },
    [revalidate, sendNotification]
  );

  const file = useCallback(
    async ({
      spaceId,
      key,
      filedOn,
    }: {
      spaceId: string;
      key: string;
      filedOn: string;
    }): Promise<boolean> => {
      const body: PostComptaDeadlineFilingRequestBody = { key, filedOn };
      return call(
        `/api/w/${owner.sId}/spaces/${spaceId}/compta/echeances/depot`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        "Dépôt non enregistré",
        (status) =>
          status === 409
            ? "Cette échéance est déjà marquée déposée."
            : status === 400
              ? "Cette échéance n'est plus dans le calendrier du dossier, ou la date est invalide."
              : status === 404
                ? "Ce dossier n'est plus accessible en écriture. Vérifiez vos droits sur cet espace."
                : "Le service est momentanément indisponible. Réessayez dans quelques minutes."
      );
    },
    [call, owner.sId]
  );

  const unfile = useCallback(
    async ({
      spaceId,
      key,
    }: {
      spaceId: string;
      key: string;
    }): Promise<boolean> =>
      call(
        `/api/w/${owner.sId}/spaces/${spaceId}/compta/echeances/depot/${encodeURIComponent(key)}`,
        { method: "DELETE" },
        "Dépôt non annulé",
        (status) =>
          status === 404
            ? "Ce dépôt n'existe plus, ou ce dossier n'est plus accessible en écriture."
            : "Le service est momentanément indisponible. Réessayez dans quelques minutes."
      ),
    [call, owner.sId]
  );

  return { file, unfile, isFiling };
}

/** The Tableaux de bord figures (série 2, lot D5a), cabinet or one dossier. */
export function useComptaDashboard({
  owner,
  period,
  spaceId,
  disabled,
}: {
  owner: LightWorkspaceType;
  period: ComptaDashboardPeriod;
  spaceId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const dashboardFetcher: Fetcher<GetComptaDashboardResponseBody> = fetcher;
  const query = new URLSearchParams({ period });
  if (spaceId !== null) {
    query.set("spaceId", spaceId);
  }

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/tableau-de-bord?${query}`,
    dashboardFetcher
  );

  return {
    dashboard: data ?? null,
    isDashboardLoading: !error && !data && !disabled,
    isDashboardError: !!error,
    mutateDashboard: mutate,
  };
}

/** The révisions of the cabinet's current fiscal years (série 2, lot D5b). */
export function useComptaCabinetMissions({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const missionsFetcher: Fetcher<GetComptaCabinetMissionsResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled ? null : `/api/w/${owner.sId}/compta/missions`,
    missionsFetcher,
    {
      // Same polling as the dossier's list: while a révision runs, the board
      // follows it to the end instead of freezing on « En cours ».
      refreshInterval: (
        latest: GetComptaCabinetMissionsResponseBody | undefined
      ) =>
        latest?.missions.some(
          (m) => m.status === "pending" || m.status === "running"
        )
          ? 5_000
          : 0,
    }
  );

  return {
    missions: useMemo(
      () => data?.missions ?? emptyArray<ComptaCabinetMissionType>(),
      [data]
    ),
    today: data?.today ?? null,
    isMissionsLoading: !error && !data && !disabled,
    isMissionsError: !!error,
    mutateMissions: mutate,
  };
}

/**
 * Resume a failed révision from the Travaux kanban (série 2, lot D5b): the
 * card carries its own space and mission. Then every missions list (the
 * cabinet's and the dossier's) revalidates.
 */
export function useResumeCabinetMission({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const { mutate } = useSWRConfig();
  const [isResuming, setIsResuming] = useState(false);

  const resumeMission = useCallback(
    async ({
      spaceId,
      missionId,
    }: {
      spaceId: string;
      missionId: string;
    }): Promise<boolean> => {
      setIsResuming(true);
      try {
        const ok = await postResumeMission({
          owner,
          podId: spaceId,
          missionId,
          sendNotification,
        });
        if (ok) {
          await mutate(
            (key) => isString(key) && key.includes("/compta/missions")
          );
        }
        return ok;
      } finally {
        setIsResuming(false);
      }
    },
    [mutate, owner, sendNotification]
  );

  return { resumeMission, isResuming };
}

// The cabinet list of pièces (lot D6), for Portefeuille · Fichiers.
export function useComptaCabinetDocuments({
  owner,
  spaceId,
  filter,
  disabled,
}: {
  owner: LightWorkspaceType;
  spaceId: string | null;
  filter: ComptaCabinetDocumentFilter;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const documentsFetcher: Fetcher<GetComptaCabinetDocumentsResponseBody> =
    fetcher;
  const params = new URLSearchParams({ filter });
  if (spaceId !== null) {
    params.set("spaceId", spaceId);
  }
  const { data, error, mutate } = useSWRWithDefaults(
    disabled
      ? null
      : `/api/w/${owner.sId}/compta/documents?${params.toString()}`,
    documentsFetcher,
    // A segment or dossier switch is a new key: keep the previous page (and
    // its counts, which feed the tab badge) until the next one lands, rather
    // than blinking the table to a skeleton.
    { keepPreviousData: true }
  );

  return {
    documents: useMemo(
      () => data?.documents ?? emptyArray<ComptaCabinetDocumentType>(),
      [data]
    ),
    totalCount: data?.totalCount ?? 0,
    toAttachCount: data?.toAttachCount ?? 0,
    isDocumentsLoading: !error && !data && !disabled,
    isDocumentsError: !!error,
    mutateDocuments: mutate,
  };
}

// The carry-over of the à-nouveaux of an exercice (lot 8a), for the Cahier.
export function useComptaOpeningBalance({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const openingFetcher: Fetcher<GetComptaOpeningBalanceResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    disabled || fiscalYearId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/opening-balance?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
    openingFetcher
  );

  return {
    openingBalance: data?.openingBalance ?? null,
    isOpeningBalanceLoading:
      !error && !data && !disabled && fiscalYearId !== null,
    isOpeningBalanceError: !!error,
  };
}

// The corporate-tax estimate of an exercice (série 2, lot 8b), its settings
// and whether the year is closed. Read-only: the gestures go through
// `useComptaCorporateTaxActions`, which revalidates this key.
export function useComptaCorporateTax({
  owner,
  podId,
  fiscalYearId,
  disabled,
}: {
  owner: LightWorkspaceType;
  podId: string;
  fiscalYearId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const corporateTaxFetcher: Fetcher<GetComptaCorporateTaxResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    disabled || fiscalYearId === null
      ? null
      : `/api/w/${owner.sId}/spaces/${podId}/compta/corporate-tax?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
    corporateTaxFetcher
  );

  return {
    corporateTax: data ?? null,
    isCorporateTaxLoading:
      !error && !data && !disabled && fiscalYearId !== null,
    isCorporateTaxError: !!error,
    mutateCorporateTax: mutate,
  };
}

const CORPORATE_TAX_UNREACHABLE =
  "Ce dossier n'est plus accessible. Vérifiez vos droits sur cet espace.";
const CORPORATE_TAX_CLOSED =
  "Cet exercice est clôturé : son estimation d'IS ne se modifie plus.";
const CORPORATE_TAX_UNAVAILABLE =
  "Le service est momentanément indisponible. Réessayez dans quelques minutes.";

function corporateTaxMessage(status: number): string {
  if (status === 404) {
    return CORPORATE_TAX_UNREACHABLE;
  }
  if (status === 409) {
    return CORPORATE_TAX_CLOSED;
  }
  if (status === 400) {
    return "La saisie n'est pas valide : un montant positif en euros, un libellé de 200 caractères au plus.";
  }
  return CORPORATE_TAX_UNAVAILABLE;
}

// The gestures on the corporate-tax estimate (lot 8b): settings, adjustments.
// Each success revalidates the estimate of the dossier.
export function useComptaCorporateTaxActions({
  owner,
  podId,
}: {
  owner: LightWorkspaceType;
  podId: string;
}) {
  const sendNotification = useSendNotification();
  const { mutate } = useSWRConfig();
  const [isWorking, setIsWorking] = useState(false);
  const url = `/api/w/${owner.sId}/spaces/${podId}/compta/corporate-tax`;

  const call = useCallback(
    async (
      path: string,
      init: RequestInit,
      title: string
    ): Promise<Response | null> => {
      setIsWorking(true);
      try {
        const response = await clientFetch(path, init);
        if (!response.ok) {
          sendNotification({
            type: "error",
            title,
            description: corporateTaxMessage(response.status),
          });
          return null;
        }
        await mutate((key) => isString(key) && key.startsWith(`${url}?`));
        return response;
      } catch {
        sendNotification({
          type: "error",
          title,
          description: "Le serveur n'a pas répondu. Réessayez.",
        });
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    [mutate, sendNotification, url]
  );

  const updateSettings = useCallback(
    async (body: PatchComptaCorporateTaxRequestBody): Promise<boolean> => {
      const response = await call(
        url,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        "Paramètres d'IS non enregistrés"
      );
      return response !== null;
    },
    [call, url]
  );

  const addAdjustment = useCallback(
    async (body: PostComptaTaxAdjustmentRequestBody): Promise<boolean> => {
      const response = await call(
        `${url}/adjustments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
        "Ajustement non enregistré"
      );
      return response !== null;
    },
    [call, url]
  );

  const removeAdjustment = useCallback(
    async (adjustmentId: string, fiscalYearId: string): Promise<boolean> => {
      const response = await call(
        `${url}/adjustments/${encodeURIComponent(adjustmentId)}?fiscalYearId=${encodeURIComponent(fiscalYearId)}`,
        { method: "DELETE" },
        "Ajustement non retiré"
      );
      return response !== null;
    },
    [call, url]
  );

  return { updateSettings, addAdjustment, removeAdjustment, isWorking };
}
